import React from 'react';
import { SortableContainer, SortableElement, SortEndHandler } from 'react-sortable-hoc';

import i18n from '@client/utils/i18n';
import { ProjectSectionStatus, RecipeSection } from '@ledium/types';
import Paper from '@mui/material/Paper';
import makeStyles from '@mui/styles/makeStyles';

type RecipeSectionDisplay = RecipeSection & {
    sectionColor?: { background?: string; text?: string };
};

interface RecipeSectionsProps {
    sectionList: RecipeSectionDisplay[];
    onSort?: SortEndHandler;
    onSectionClick?: (section: RecipeSectionDisplay) => void;
    status?: ProjectSectionStatus | null;
    readOnly?: boolean;
    passive?: boolean;
}

interface SortableItemProps {
    value: RecipeSectionDisplay;
}

interface SortableListItemsProps {
    items: RecipeSectionDisplay[];
}

const repeatBorder = '1px solid rgba(0, 0, 0, 0.15)';
const useStyles = makeStyles(() => ({
    sectionContainer: {
        display: 'inline-block',
        cursor: 'pointer'
    },
    repeatContainer: {
        height: '20px',
        borderRight: repeatBorder
    },
    repeatItem: {
        display: 'inline-block',
        position: 'relative',
        borderLeft: repeatBorder,
        textAlign: 'center',
        height: '20px'
    },
    repeatPosition: {
        position: 'absolute',
        height: '100%',
        width: 0,
        background: '#000',
        opacity: '0.5'
    },
    recipeConfigContainer: {
        display: 'flex',
        flexWrap: 'nowrap',
        justifyContent: 'space-between',
        padding: 0
    },
    sectionName: {
        textAlign: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        padding: '0 5px',
        lineHeight: '30px',
        borderBottom: repeatBorder,
        whiteSpace: 'nowrap'
    },
    sectionDesc: {
        lineHeight: '25px',
        fontSize: '11px',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    }
}));

const RecipeSections = ({ sectionList, onSort, onSectionClick, status, readOnly, passive }: RecipeSectionsProps) => {
    const classes = useStyles();
    const fullLength = sectionList.map((e) => e.repeatNum).reduce((a, c) => a + c, 0);

    const repeatItemWidth = (sectionId: string, repeatNum: number) => {
        if (!status) {
            return 0;
        }
        if (sectionId < status.id) {
            return 100;
        }
        if (sectionId > status.id) {
            return 0;
        }
        if (repeatNum < status.currentRepeatNum) {
            return 100;
        }
        if (repeatNum > status.currentRepeatNum) {
            return 0;
        }
        return status.sectionPercent;
    };

    const SectionItem = ({ section }: { section: RecipeSectionDisplay }) => (
        // eslint-disable-next-line
        <li
            onClick={() => onSectionClick && onSectionClick(section)}
            className={classes.sectionContainer}
            style={{ width: `${(98 / fullLength) * section.repeatNum}%` }}
        >
            <Paper square elevation={0} style={{ backgroundColor: section.sectionColor?.background, color: section.sectionColor?.text }}>
                <div className={classes.sectionName}>
                    {section.name}
                    <div className={classes.sectionDesc}>
                        {section.repeatNum} x {section.circadianLength} {i18n('circadian_length_hours')}
                    </div>
                </div>
                <div className={classes.repeatContainer}>
                    {[...Array(section.repeatNum)].map((e, i) => (
                        <div key={i} className={classes.repeatItem} style={{ width: `${100 / section.repeatNum}%` }}>
                            {status ? <div className={classes.repeatPosition} style={{ width: `${repeatItemWidth(section.id, i + 1)}%` }} /> : null}
                        </div>
                    ))}
                </div>
            </Paper>
        </li>
    );

    // Sortable wrappers are extracted to named functions to avoid prettier confusing TypeScript
    // generic syntax with JSX in .tsx files when the argument contains JSX
    function sortableItemRenderer(props: SortableItemProps) {
        return <SectionItem key={`section-${props.value.id}`} section={props.value} />;
    }
    function sortableListRenderer(props: SortableListItemsProps) {
        return (
            <ul className={classes.recipeConfigContainer}>
                {props.items.map((value: RecipeSectionDisplay, index: number) => (
                    // eslint-disable-next-line
                    <SortableElementComponent disabled={!onSort || !!readOnly} key={`item-${value.id}`} index={index} value={value} />
                ))}
            </ul>
        );
    }

    // eslint-disable-next-line
    const SortableElementComponent = SortableElement(sortableItemRenderer) as React.ComponentClass<
        SortableItemProps & import('react-sortable-hoc').SortableElementProps
    >;
    // eslint-disable-next-line
    const SortableList = SortableContainer(sortableListRenderer) as React.ComponentClass<
        SortableListItemsProps & import('react-sortable-hoc').SortableContainerProps
    >;

    return readOnly || passive ? (
        <ul className={classes.recipeConfigContainer}>
            {sectionList.map((section) => (
                <SectionItem key={section.id} section={section} />
            ))}
        </ul>
    ) : (
        <>
            <SortableList items={sectionList} distance={5} onSortEnd={onSort} axis="x" lockAxis="x" />
            {sectionList.length > 1 && onSort && !readOnly ? (
                <p style={{ textAlign: 'center' }}>{i18n('you_can_change_the_order_of_the_sections_by_drag_n_drop')}</p>
            ) : null}
        </>
    );
};

export default RecipeSections;
